import { isBusinessOpen, primaryProperty, useDeleteBusiness, useGetAllBusinesses } from "@api/businessService";
import { useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import DirectionsRoundedIcon from '@mui/icons-material/DirectionsRounded';
import { formatDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";

const columns = [
    {
        id: 'name',
        numeric: false,
        disablePadding: true,
        sortable: true,
        label: 'Name',
        cell: (row, {LinkComponent}) => {
            return (<>
                <LinkComponent to={`${row.id}`} relative={true} object={row}>
                    <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.name}</Typography>
                </LinkComponent>

                {row.dba_name && (<Typography fontSize="sm"><Typography fontWeight={'bold'}>DBA</Typography> {row.dba_name}</Typography>)}

                {row.owner && (<Typography fontSize="sm"><Typography fontWeight={'bold'}>Primary Contact</Typography> <LinkComponent to={`/contacts/${row.owner.id}`} relative={true} object={row.owner}>{row.owner.name}</LinkComponent></Typography>)}
            </>)
        }
      },
      {
        id: 'address',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Address',
        cell: (row, {LinkComponent}) => {
            if(!row.business_locations.length) {
                return null;
            }

            const primary = primaryProperty(row.business_locations);

            return (
                <LinkComponent to={`/properties/${primary.id}`} relative={true} object={primary}>
                    <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{primary.address}</Typography>
                </LinkComponent>
            );
        }
      },
      {
        id: 'hours',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Hours',
        cell: (row) => isBusinessOpen(row.hours),
      },
];

const listCell = (row, {LinkComponent}) => {
    let primary = null;
    if(row.business_locations.length) {
        primary = primaryProperty(row.business_locations);
    }

    return (<>
        <Box sx={{mb: 1}}>
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                <LinkComponent to={`${row.id}`} relative={true} object={row}>
                    {row.name}
                </LinkComponent>
            </Typography>
            {primary && (
                <LinkComponent to={`/properties/${primary.id}`} object={primary}>
                    <Typography fontSize="sm">{primary.address}</Typography>
                </LinkComponent>
            )}

            {isBusinessOpen(row.hours)}
        </Box>
    </>);
};

const listActions = (row, {view, LinkComponent, ViewAction}) => {
    if(view === 'deleted') {
        return null;
    }

    let destination = null;
    if(row.business_locations) {
        const primary = primaryProperty(row.business_locations);
        if(primary) {
            destination = `${primary.address} ${primary.city} ${primary.state_province}`;
        }
    }

    return (<>
        <Tooltip title="Get Directions">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!destination}
                component={'a'}
                href={`https://www.google.com/maps/dir/?api=1&destination=${destination}`}
                target="_blank"
                >
                <DirectionsRoundedIcon />
            </IconButton>
        </Tooltip>
        <Tooltip title="Call">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!row.phone}
                component={'a'}
                href={`tel:${row.phone}`}
                >
                <LocalPhoneRoundedIcon />
            </IconButton>
        </Tooltip>
    </>);
};


const viewModes = [
    {
        name: 'active',
        label: 'Active',
        tooltip: 'All active businesses',
        filters: {
            'close_date': 'null',
            'moved_out_date': 'null',
            'not_in_district': 'null',
            'filter_mode': 'and',
        },
    },
    {
        name: 'all',
        label: 'All',
        tooltip: 'All businesses, including closed',
    },
    {
        name: 'deleted',
        label: 'Deleted',
        tooltip: 'Deleted businesses'
    },
];


export default function BusinessIndex() {
    return (
        <ObjectIndex
            query={useGetAllBusinesses}
            deleteMutation={useDeleteBusiness}
            type={'business'}
            permissionType={'businesses'}
            title={'Businesses'}
            color={'business'}
            columns={columns}
            viewModes={viewModes}
            listCell={listCell}
            listActions={listActions}
        />
    )
}
